import { Alert, Box, Button, FormikTextInput, TextLink, toast } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AdderType,
  HardwareType,
  MilestoneType,
  MonitoringAccessReason,
  MonitoringAccessResult,
  RequirementFlagSettingsType,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useCheckMonitoringAccessMutation } from '../../services/sundial';
import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import FormSection from '../Forms/FormSection';
import { FlagBox } from '../QueueFlags/FlagBox';

interface MonitoringActiveProps {
  adders: AdderType[];
  accountId: string;
  milestoneType: MilestoneType.install | MilestoneType.activation;
  isMonitoringRequired?: boolean;
  isPackageEditable?: boolean;
}

const MONITORING_HELP_URL: Record<MilestoneType.install | MilestoneType.activation, string> = {
  [MilestoneType.install]:
    'https://help.palmetto.finance/en/articles/11705727-solar-energy-plan-monitoring-portal-access-m1',
  [MilestoneType.activation]:
    'https://help.palmetto.finance/en/articles/13871425-solar-energy-plan-m2-monitoring-data-troubleshooting',
};

interface MonitoringActiveFormValues {
  monitoringSiteId: string;
  batteryMonitoringSiteId?: string;
}

const easeMessage = 'You may still submit this package without access granted.';

const REASON_MESSAGES: Record<MonitoringAccessReason, string> = {
  [MonitoringAccessReason.ACCESS_NOT_GRANTED]: `Palmetto does not have access to this monitoring system. Verify the ID is correct and site access is shared with Palmetto. ${easeMessage}`,
  [MonitoringAccessReason.VALIDATED_NO_EQUIPMENT]: `Hardware details are not yet available. You may still submit this package.`,
  [MonitoringAccessReason.SUNDIAL_TIMEOUT]: `Unable to reach the monitoring portal. ${easeMessage}`,
  [MonitoringAccessReason.SUNDIAL_UNAVAILABLE]: `Unable to reach the monitoring portal. ${easeMessage}`,
  [MonitoringAccessReason.KEYSTONE_UNAVAILABLE]: `Unable to reach the monitoring portal. ${easeMessage}`,
  [MonitoringAccessReason.ALREADY_CONNECTED]: `Verification failed. Please check the ID and try again. ${easeMessage}`,
  [MonitoringAccessReason.INVALID_FORMAT]: `Invalid monitoring ID format. ${easeMessage}`,
};

const OEM_URLS: Record<string, string> = {
  solaredge: `https://monitoring.solaredge.com/one#/residential/dashboard?siteId={id}`,
  enphase: `https://enlighten.enphaseenergy.com/systems/{id}/graphs`,
  tesla: `https://powerhub.energy.tesla.com/site/{id}/graph-v2`,
  qcells: `https://fleet.es.qcells.com/FleetSiteDetails/?id={id}`,
  generac: `https://pwrfleet.generac.com/sites/{id}`,
};

const assignURL = (manufacturer: string, id: string) => {
  const baseUrl = OEM_URLS[manufacturer.toLowerCase()];
  if (baseUrl) {
    return baseUrl.replace('{id}', id);
  }
  return undefined;
};

const getReasonMessage = (reason: MonitoringAccessReason | undefined, reasonDetail: string | undefined): string => {
  if (reasonDetail) return reasonDetail;
  if (!reason) return `Verification failed. Please check the ID and try again. ${easeMessage}`;
  return REASON_MESSAGES[reason] ?? `Verification failed. Please check the ID and try again. ${easeMessage}`;
};

const getEquipmentMessage = (result: MonitoringAccessResult): string | undefined => {
  const { equipment } = result;
  if (!equipment) return undefined;
  return (
    [
      ...equipment.inverters.map(
        (inv) => `${inv.manufacturer} ${inv.model} ${inv.serialNumber ? `— ${inv.serialNumber}` : ''}`,
      ),
      ...equipment.batteries.map(
        (bat) => `${bat.manufacturer} ${bat.model} ${bat.serialNumber ? `— ${bat.serialNumber}` : ''}`,
      ),
      ...equipment.gateways.map(
        (gw) => `${gw.manufacturer} ${gw.model} ${gw.serialNumber ? `— ${gw.serialNumber}` : ''}`,
      ),
    ].join('\n') || undefined
  );
};

const getSuccessMessage = (result: MonitoringAccessResult): string | undefined => {
  if (result.reason === MonitoringAccessReason.VALIDATED_NO_EQUIPMENT) {
    return REASON_MESSAGES[MonitoringAccessReason.VALIDATED_NO_EQUIPMENT];
  }
  return getEquipmentMessage(result);
};

export const MonitoringActive = ({
  adders,
  accountId,
  milestoneType,
  isMonitoringRequired = true,
  isPackageEditable = true,
}: MonitoringActiveProps) => {
  const { flagDrivenPackageReview = false, monitoringIdAccessCheck = false } = useFlags();
  const { claims } = useAuth();
  const isInternalUser = isPalmettoFinanceUser(claims);
  const { data: account } = useGetAccountQuery(accountId);
  const inverterManufacturer =
    account?.systemDesign?.inverters?.[0]?.manufacturer || account?.systemDesign?.inverterManufacturer;
  const batteryManufacturer = account?.systemDesign?.storage?.[0]?.manufacturer;
  const hasBattery = useMemo(
    () => adders?.includes(AdderType.arbitrageBattery) || adders?.includes(AdderType.backupBattery),
    [adders],
  );
  const { isSubmitting, values } = useFormikContext<MonitoringActiveFormValues>();
  const [checkSolarAccess, { isLoading: isSolarChecking }] = useCheckMonitoringAccessMutation();
  const [checkBatteryAccess, { isLoading: isBatteryChecking }] = useCheckMonitoringAccessMutation();
  const [solarResult, setSolarResult] = useState<MonitoringAccessResult | null>(null);
  const [batteryResult, setBatteryResult] = useState<MonitoringAccessResult | null>(null);

  const solarId = values.monitoringSiteId?.trim() ?? '';
  const batteryId = values.batteryMonitoringSiteId?.trim() ?? '';

  const inverterOEMUrl = inverterManufacturer ? assignURL(inverterManufacturer, solarId) : undefined;
  const batteryOEMUrl = batteryManufacturer ? assignURL(batteryManufacturer, batteryId) : undefined;

  const handleCheckSolarAccess = useCallback(async () => {
    if (!solarId) return;
    try {
      const response = await checkSolarAccess({
        accountId,
        monitoringIds: [{ id: solarId, type: HardwareType.inverter }],
      }).unwrap();
      setSolarResult(response.results[0]);
    } catch (error) {
      setSolarResult(null);
      const message =
        error instanceof Error && error.message ? error.message : 'An unexpected error occurred. Please try again.';
      toast.error(message);
    }
  }, [accountId, solarId, checkSolarAccess]);

  const handleCheckBatteryAccess = useCallback(async () => {
    if (!batteryId) return;
    try {
      const response = await checkBatteryAccess({
        accountId,
        monitoringIds: [{ id: batteryId, type: HardwareType.battery }],
      }).unwrap();
      setBatteryResult(response.results[0]);
    } catch (error) {
      setBatteryResult(null);
      const message =
        error instanceof Error && error.message ? error.message : 'An unexpected error occurred. Please try again.';
      toast.error(message);
    }
  }, [accountId, batteryId, checkBatteryAccess]);

  useEffect(() => {
    setSolarResult(null);
  }, [solarId]);

  useEffect(() => {
    setBatteryResult(null);
  }, [batteryId, hasBattery]);

  return (
    <FormSection
      title="Monitoring & System Settings"
      description={
        <Box childGap="xs" color="body-secondary">
          <p>
            {isMonitoringRequired ? <b>Required:</b> : <b>Optional:</b>} Monitoring portal ID for the inverter (and
            battery, if applicable).
          </p>
          <p>Ensure site access is shared to Palmetto as owner & CTs are configured.</p>
          <p>
            <TextLink href={MONITORING_HELP_URL[milestoneType]} target="_blank" rel="noreferrer">
              See monitoring portal access requirements
            </TextLink>
          </p>
        </Box>
      }
    >
      <FlagBox
        dataKey="Inverter Monitoring ID"
        flagType={RequirementFlagSettingsType.inverterMonitoringId}
        displayWithNoQueue
      >
        <Box
          direction={{ base: 'column', tablet: 'row' }}
          alignItems={{ base: 'flex-start', tablet: 'center' }}
          childGap={{ base: 'lg', tablet: '3xl' }}
          width="100"
        >
          <Field
            type="text"
            name="monitoringSiteId"
            id="monitoringSiteId"
            hideLabel
            isRequired={isMonitoringRequired}
            component={FormikTextInput}
            isDisabled={isSubmitting || isSolarChecking || !isPackageEditable}
          />
          {monitoringIdAccessCheck && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={handleCheckSolarAccess}
              isDisabled={!solarId || isSubmitting}
              isLoading={isSolarChecking}
            >
              {solarResult?.verified ? 'Verified' : 'Check Access'}
            </Button>
          )}
        </Box>
        {monitoringIdAccessCheck && solarResult && (
          <Box margin="sm 0 0 0">
            {solarResult.verified ? (
              <>
                <Alert variant="success" title={`Access confirmed`} message={getSuccessMessage(solarResult)} />
                {isInternalUser && inverterOEMUrl && (
                  <Box margin="xs 0 0 0">
                    <TextLink href={inverterOEMUrl} target="_blank" rel="noreferrer">
                      View in OEM Monitoring Portal
                    </TextLink>
                  </Box>
                )}
              </>
            ) : (
              <Alert
                variant="info"
                hasIcon={true}
                message={getReasonMessage(solarResult.reason, solarResult.reasonDetail)}
              />
            )}
          </Box>
        )}
      </FlagBox>
      {flagDrivenPackageReview && (
        <Box alignItems="flex-end" padding={'md 0 0 0'}>
          <Box width={{ base: '100', tablet: '4xl' }} gap="md">
            <FlagBox
              dataKey="Access to LightReach - Inverter"
              flagType={RequirementFlagSettingsType.accessToLightReachInverter}
            />
            <FlagBox
              dataKey="Configuration, Settings, and Layout - Inverter"
              flagType={RequirementFlagSettingsType.configurationSettingsLayoutInverter}
            />
          </Box>
        </Box>
      )}
      {hasBattery && (
        <>
          <FlagBox
            dataKey="Battery Monitoring ID"
            flagType={RequirementFlagSettingsType.batteryMonitoringId}
            displayWithNoQueue
          >
            <Box
              direction={{ base: 'column', tablet: 'row' }}
              alignItems="center"
              childGap={{ base: 'lg', tablet: '3xl' }}
              width="100"
            >
              <Field
                type="text"
                name="batteryMonitoringSiteId"
                id="batteryMonitoringSiteId"
                hideLabel
                isRequired={isMonitoringRequired}
                component={FormikTextInput}
                isDisabled={isSubmitting || isBatteryChecking || !isPackageEditable}
              />
              {monitoringIdAccessCheck && (
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={handleCheckBatteryAccess}
                  isDisabled={!batteryId || isSubmitting}
                  isLoading={isBatteryChecking}
                >
                  {batteryResult?.verified ? 'Verified' : 'Check Access'}
                </Button>
              )}
            </Box>
            {monitoringIdAccessCheck && batteryResult && (
              <Box margin="sm 0 0 0">
                {batteryResult.verified ? (
                  <>
                    <Alert variant="success" title={`Access confirmed`} message={getSuccessMessage(batteryResult)} />
                    {isInternalUser && batteryOEMUrl && (
                      <Box margin="xs 0 0 0">
                        <TextLink href={batteryOEMUrl} target="_blank" rel="noreferrer">
                          View in OEM Monitoring Portal
                        </TextLink>
                      </Box>
                    )}
                  </>
                ) : (
                  <Alert
                    variant="info"
                    hasIcon={true}
                    message={getReasonMessage(batteryResult.reason, batteryResult.reasonDetail)}
                  />
                )}
              </Box>
            )}
          </FlagBox>
          {flagDrivenPackageReview && (
            <Box alignItems="flex-end">
              <Box width={{ base: '100', tablet: '4xl' }} gap="md" margin={'md 0 0 0'}>
                <FlagBox
                  dataKey="Access to LightReach - Battery"
                  flagType={RequirementFlagSettingsType.accessToLightReachBattery}
                />
                <FlagBox
                  dataKey="Configuration and Settings - Battery"
                  flagType={RequirementFlagSettingsType.configurationSettingsBattery}
                />
              </Box>
            </Box>
          )}
        </>
      )}
    </FormSection>
  );
};
